import cx from 'classnames';
import moment from 'moment';
import React from 'react';

import {
  ButtonBase, FormControlLabel, Switch, Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';

import GridContainer from 'creative-components/Grid/GridContainer';
import GridItem from 'creative-components/Grid/GridItem';

import { useAuthDataContext } from 'components/AuthDataProvider/AuthDataProvider';
import CampaignAPNsReduceModal from 'components/CampaignInfoCards/CampaignAPNsReduceModal';
import CampaignCostsTooltip from 'components/CampaignInfoCards/CampaignCostsTooltip';

import { campaignEventTypeCanBeBilledAnnually, formatCents, numberWithCommas } from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  infoCard: {
    margin: '20px auto 0',
    padding: '10px 24px',
    borderRadius: '8px',
    position: 'relative',
    maxWidth: '315px',
    height: '70px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    fontSize: '14px',
    lineHeight: '18px',
    color: theme.palette.black.main,

    '& > h4': {
      font: 'inherit',
      fontWeight: 'bold',
      marginBottom: '4px',
    },
    '& > p': {
      font: 'inherit',
      fontWeight: 'normal',
    },
  },
  numberOfApnsInfoCard: {
    '& svg': {
      marginLeft: '10px',
      verticalAlign: 'middle',
    },
    '& > button': {
      cursor: 'pointer',
      position: 'absolute',
      bottom: '10px',
      right: '24px',
      textDecoration: 'underline',
      fontFamily: 'Overpass Mono',
      fontWeight: 600,
      fontSize: '13px',
      lineHeight: '16px',
      color: theme.palette.orange.main,
    },
  },
  targetInfoCard: {
    maxWidth: '360px',
    minHeight: '175px',
    height: 'auto',
    padding: '14px 18px 30px',
    justifyContent: 'flex-start',
  },
  targetMainText: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    minHeight: '20px',
    lineHeight: '18px !important',
    fontWeight: 'bold',
    fontSize: '16px !important',

    '& svg': {
      marginLeft: '0 !important',
      flexShrink: 0,
    },
  },
  targetBreakdownText: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginTop: 8,
  },
  targetBreakdownPill: {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: '24px',
    padding: '4px 8px',
    borderRadius: '8px',
    fontSize: '12px !important',
    lineHeight: '14px !important',
    fontWeight: 600,
    backgroundColor: 'rgba(255, 255, 255, 0.72)',
  },
  targetBreakdownPillMuted: {
    color: theme.palette.darkGray.main,
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
  },
  targetToggle: {
    display: 'flex',
    gap: '8px',
    width: '100%',
    marginTop: '14px',
    '& > button': {
      flex: 1,
      minHeight: '36px',
      border: `1px solid ${theme.palette.lightGray.main}`,
      borderRadius: '8px',
      padding: '6px 8px',
      fontFamily: 'inherit',
      fontWeight: 700,
      fontSize: '12px',
      lineHeight: '14px',
      textAlign: 'center',
      color: theme.palette.darkGray.main,
      backgroundColor: theme.palette.white.main,
    },
  },
  targetToggleLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  targetToggleInfoIcon: {
    marginLeft: '0 !important',
    fontSize: '16px',
    flexShrink: 0,
  },
  targetToggleSelected: {
    borderColor: `${theme.palette.orange.main} !important`,
    color: `${theme.palette.orange.main} !important`,
  },
  warningIcon: {
    color: theme.palette.warning.main,
  },
  pricingInfoCard: {
    '& > p': {
      fontSize: '14px',
      lineHeight: '18px',
    },
    '& > span': {
      cursor: 'grab',
      position: 'absolute',
      bottom: '10px',
      right: '24px',
      textDecoration: 'underline',
      fontFamily: 'Overpass Mono',
      fontWeight: 600,
      fontSize: '12px',
      lineHeight: '16px',
      color: theme.palette.orange.main,
    },
  },
  annualBillingSwitch: {
    position: 'absolute',
    bottom: '20px',
    left: '24px',
    '& > label > span': {
      color: theme.palette.darkGray.main,
      fontSize: '13px',
      fontWeight: 500,
    },
  },
}));

const CampaignStartDateCard = ({ startDate }) => {
  const classes = useStyles();

  return (
    <div className={classes.infoCard} style={{ backgroundColor: '#e5fde2' }}>
      <h4>Campaign Start Date</h4>
      <p>{startDate ? moment(startDate).format('MMMM Do, YYYY') : 'TBD'}</p>
    </div>
  );
};

const CampaignEndDateCard = ({ endDate }) => {
  const classes = useStyles();

  return (
    <div className={classes.infoCard} style={{ backgroundColor: '#D5FCCF' }}>
      <h4>Campaign End Date</h4>
      <p>{endDate ? moment(endDate).format('MMMM Do, YYYY') : 'TBD'}</p>
    </div>
  );
};

const NumberOfApnsCard = ({
  apnsTotalSelectedCount,
  apnsTotalInAreaCount,
  valuesAreEstimates,
  apnsFarmingStoppedCount,
  isCreating,
  apnAvailableCounts = {},
  onReduceClick,
  showHighPropensityTargeting,
  useHighPropensityToSell,
  highPropensityAvailableCount,
  allAvailableCount,
  addOnPropertiesCount = 0,
  showOverlapWarning = false,
  onTargetingChange,
}) => {
  const classes = useStyles();

  const hasAvailableCounts = Object.keys(apnAvailableCounts).length !== 0;
  const farmAreaSelectedCount = Math.max(apnsTotalSelectedCount - addOnPropertiesCount, 0);
  const showAddOnOnlyBreakdown = !showHighPropensityTargeting && addOnPropertiesCount > 0 && farmAreaSelectedCount === 0;
  const showAreaCountTooltip = !showHighPropensityTargeting
    && apnsTotalInAreaCount !== undefined
    && apnsTotalSelectedCount < apnsTotalInAreaCount;
  const showActiveCountTooltip = !showHighPropensityTargeting && !valuesAreEstimates && apnsFarmingStoppedCount > 0;
  const shouldShowFallbackCount = !showHighPropensityTargeting && apnsTotalInAreaCount === undefined;

  let cardTitleSuffix = '';
  if (valuesAreEstimates) cardTitleSuffix = ' (est.)';

  let mainCountContent = null;
  if (showHighPropensityTargeting || showAddOnOnlyBreakdown || showAreaCountTooltip || apnsTotalInAreaCount !== undefined) {
    mainCountContent = `${numberWithCommas(apnsTotalSelectedCount)} APNs`;
  }

  let supplementalCountContent = null;
  if (showAddOnOnlyBreakdown) {
    supplementalCountContent = null;
  } else if (showActiveCountTooltip) {
    supplementalCountContent = `${numberWithCommas(apnsTotalSelectedCount)} Total, ${numberWithCommas(apnsTotalSelectedCount - apnsFarmingStoppedCount)} Active`;
  } else if ((!showHighPropensityTargeting && !valuesAreEstimates) || shouldShowFallbackCount) {
    supplementalCountContent = numberWithCommas(apnsTotalSelectedCount);
  }

  const targetPrimaryBreakdownContent = showHighPropensityTargeting
    ? `${numberWithCommas(farmAreaSelectedCount)} ${useHighPropensityToSell ? 'high propensity to sell' : 'available'}`
    : null;
  const targetExtraBreakdownContent = addOnPropertiesCount > 0
    ? `+ ${numberWithCommas(addOnPropertiesCount)} extra uploaded`
    : null;

  return (
    <div
      className={cx(
        classes.infoCard,
        classes.numberOfApnsInfoCard,
        { [classes.targetInfoCard]: !!targetPrimaryBreakdownContent || !!targetExtraBreakdownContent },
      )}
      style={{
        backgroundColor: '#D6F5FF',
      }}
    >
      <h4>{`Target APNs${cardTitleSuffix}`}</h4>
      <p className={showHighPropensityTargeting ? classes.targetMainText : undefined}>
        <>
          {mainCountContent}

          {showOverlapWarning && (
            <Tooltip
              placement="top"
              disableFocusListener
              disableTouchListener
              title="You may have overlapping APNs in your campaign's farm area. We will remove any duplicate APNs, and if we do, the actual number of APNs in your campaign may be lower than the estimate shown here."
            >
              <WarningRoundedIcon className={classes.warningIcon} />
            </Tooltip>
          )}

          {showAreaCountTooltip && (
            <Tooltip
              placement="top"
              disableFocusListener
              disableTouchListener
              title={`There are a total of ${numberWithCommas(apnsTotalInAreaCount)} APNs in the selected Farm Areas, some of which may be farmed by other users and be unavailable to you, or excluded from your campaign due to your APN limit`}
            >
              <InfoOutlinedIcon />
            </Tooltip>
          )}

          {supplementalCountContent && (
            <span>
              {supplementalCountContent}
            </span>
          )}

          {showActiveCountTooltip && (
            <Tooltip
              placement="top"
              disableFocusListener
              disableTouchListener
              title={`The total number of APNs less the ${apnsFarmingStoppedCount} you've disabled farming on`}
            >
              <InfoOutlinedIcon />
            </Tooltip>
          )}
        </>
      </p>

      {showAddOnOnlyBreakdown && (
        <div className={classes.targetBreakdownText}>
          <span className={cx(classes.targetBreakdownPill, classes.targetBreakdownPillMuted)}>
            {`+ ${numberWithCommas(addOnPropertiesCount)} extra uploaded`}
          </span>
        </div>
      )}

      {showHighPropensityTargeting && (
        <>
          <div className={classes.targetBreakdownText}>
            <span className={classes.targetBreakdownPill}>
              {targetPrimaryBreakdownContent}
            </span>
            {targetExtraBreakdownContent && (
              <span className={cx(classes.targetBreakdownPill, classes.targetBreakdownPillMuted)}>
                {targetExtraBreakdownContent}
              </span>
            )}
          </div>
          <div className={classes.targetToggle}>
            <ButtonBase
              className={useHighPropensityToSell ? classes.targetToggleSelected : undefined}
              onClick={() => onTargetingChange(true)}
            >
              <span className={classes.targetToggleLabel}>
                High propensity
                <Tooltip
                  placement="top"
                  disableFocusListener
                  disableTouchListener
                  title="Prioritizes properties with longer ownership, high mortgage rates, and low loan-to-value ratios."
                >
                  <InfoOutlinedIcon className={classes.targetToggleInfoIcon} />
                </Tooltip>
              </span>
            </ButtonBase>
            <ButtonBase
              className={!useHighPropensityToSell ? classes.targetToggleSelected : undefined}
              onClick={() => onTargetingChange(false)}
            >
              <span className={classes.targetToggleLabel}>
                All available
                <Tooltip
                  placement="top"
                  disableFocusListener
                  disableTouchListener
                  title="Targets all available APNs in this farm area, excluding APNs already being farmed by other users."
                >
                  <InfoOutlinedIcon className={classes.targetToggleInfoIcon} />
                </Tooltip>
              </span>
            </ButtonBase>
          </div>
        </>
      )}

      {isCreating && onReduceClick && apnsTotalSelectedCount > 0 && hasAvailableCounts && (
        <ButtonBase onClick={onReduceClick}>Reduce?</ButtonBase>
      )}
    </div>
  );
};

export const CampaignCostCard = ({
  valuesAreEstimates,
  campaignCosts,
  apnsTotalSelectedCount,
  isCreating,
  eventType,
  setIsAnnualBilling,
}) => {
  const classes = useStyles();

  let cardTitleSuffix = '';
  if (valuesAreEstimates) cardTitleSuffix = ' (est.)';

  // If they're on the create campaign screen, they can choose between annual/monthly billing.
  // Show a different screen for this case by checking the setIsAnnualBilling callback.
  if (isCreating) {
    return (
      <div
        className={cx(classes.infoCard, classes.pricingInfoCard)}
        style={{
          backgroundColor: '#B3EDFF',
          height: '125px',
          marginTop: '20px',
          justifyContent: 'flex-start',
          paddingTop: '18px',
          gap: 2,
        }}
      >
        <h4>{`Campaign Cost${cardTitleSuffix}`}</h4>
        {campaignCosts && apnsTotalSelectedCount > 0 ? (
          <>
            <p>
              {campaignCosts.isAnnualBilling
                ? `${formatCents(campaignCosts.annualEffectiveMonthly)}/mo - Save ${formatCents(campaignCosts.annualDiscount)}!`
                : `${formatCents(campaignCosts.totalMonthlyCost)}/mo`}
            </p>

            {/* {campaignCosts.isAnnualBilling && (
              <h5 style={{ padding: '4px 0', fontWeight: 400, fontSize: '14px' }}>
                {`Save ${formatCents(campaignCosts.annualDiscount)}! - Effectively ${formatCents(campaignCosts.annualEffectiveMonthly)}/mo`}
              </h5>
            )} */}

            {/* Only allow annual billing toggle for campaigns that support annual billing (ABS/FF) */}
            {isCreating && campaignEventTypeCanBeBilledAnnually(eventType) && (
              <div className={classes.annualBillingSwitch}>
                <FormControlLabel
                  control={(
                    <Switch
                      color="primary"
                      checked={campaignCosts.isAnnualBilling}
                      onChange={(e) => setIsAnnualBilling(e.target.checked)}
                    />
                    )}
                  label={campaignCosts.isAnnualBilling ? 'Annual billing - 1 month free' : 'Monthly billing'}
                />
              </div>
            )}

            <span>
              <CampaignCostsTooltip
                text="Breakdown"
                campaignCosts={campaignCosts}
              />
            </span>
          </>
        ) : <p>N/A</p>}
      </div>
    );
  }

  return (
    <div className={cx(classes.infoCard, classes.pricingInfoCard)} style={{ backgroundColor: '#B3EDFF' }}>
      <h4>{`Campaign Cost${cardTitleSuffix}`}</h4>
      {campaignCosts && (
        <>
          <p>
            {campaignCosts.isAnnualBilling
              // ? `${formatCents(campaignCosts.annualTotal)} once`
              ? `${formatCents(campaignCosts.annualEffectiveMonthly)}/mo`
              : `${formatCents(campaignCosts.totalMonthlyCost)}/mo`}
          </p>

          <span>
            <CampaignCostsTooltip
              text="Breakdown"
              campaignCosts={campaignCosts}
            />
          </span>
        </>
      )}
    </div>
  );
};

function CampaignInfoCards({
  startDate, endDate, eventType, campaignCosts, valuesAreEstimates, apnsTotalSelectedCount, apnsFarmingStoppedCount, isCreating,

  // Optional, used for create campaign page
  apnAvailableCounts, apnSelectedCounts, setApnSelectedCounts, setApnsTotalSelectedCount, apnsTotalInAreaCount, setIsAnnualBilling,
  showHighPropensityTargeting, useHighPropensityToSell, highPropensityAvailableCount, allAvailableCount, showOverlapWarning, onTargetingChange,
}) {
  const { isAdmin, isAccountManager } = useAuthDataContext();

  const [reduceAPNsModalOpen, setReduceAPNsModalOpen] = React.useState(false);
  const addOnPropertiesCount = apnSelectedCounts?.find(({ name }) => name === 'Add On Properties')?.count ?? 0;

  return (
    <GridContainer justifyContent="flex-start" alignContent="center">
      {apnAvailableCounts && Object.keys(apnAvailableCounts).length !== 0 && reduceAPNsModalOpen && (
        <CampaignAPNsReduceModal
          onClose={() => setReduceAPNsModalOpen(false)}
          apnAvailableCounts={apnAvailableCounts}
          apnSelectedCounts={apnSelectedCounts}
          setApnSelectedCounts={setApnSelectedCounts}
          setApnsTotalSelectedCount={setApnsTotalSelectedCount}
        />
      )}

      <GridItem xs={12} sm={6} xl={3}>
        <CampaignStartDateCard startDate={startDate} />
      </GridItem>
      <GridItem xs={12} sm={6} xl={3}>
        <CampaignEndDateCard endDate={endDate} />
      </GridItem>
      <GridItem xs={12} sm={6} xl={3}>
        <NumberOfApnsCard
          apnsTotalSelectedCount={apnsTotalSelectedCount}
          apnsTotalInAreaCount={apnsTotalInAreaCount}
          valuesAreEstimates={valuesAreEstimates}
          apnsFarmingStoppedCount={apnsFarmingStoppedCount}
          isCreating={isCreating}
          apnAvailableCounts={apnAvailableCounts}
          onReduceClick={() => setReduceAPNsModalOpen(true)}
          showHighPropensityTargeting={showHighPropensityTargeting}
          useHighPropensityToSell={useHighPropensityToSell}
          highPropensityAvailableCount={highPropensityAvailableCount}
          allAvailableCount={allAvailableCount}
          addOnPropertiesCount={addOnPropertiesCount}
          showOverlapWarning={showOverlapWarning}
          onTargetingChange={onTargetingChange}
        />
      </GridItem>

      {/* Only show the campaign cost card with the campaign info cards for campaigns being created or for admins.
          Otherwise we stuff it under the campaign settings for started campaigns. */}
      {(isCreating || isAdmin || isAccountManager) && (
        <GridItem xs={12} sm={6} xl={3}>
          <CampaignCostCard
            valuesAreEstimates={valuesAreEstimates}
            campaignCosts={campaignCosts}
            apnsTotalSelectedCount={apnsTotalSelectedCount}
            isCreating={isCreating}
            eventType={eventType}
            setIsAnnualBilling={setIsAnnualBilling}
          />
        </GridItem>
      )}
    </GridContainer>
  );
}

export default CampaignInfoCards;
